import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { InstantTasksProvider } from '@koderlabs/tasks-sdk-react'
import { queryClient } from './lib/query-client'
import { initSentry } from './lib/sentry'
import { instantTasksEnabled, instantTasksOptions } from './lib/instant-tasks'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import './theme/globals.scss'
import './theme/app.scss'

initSentry()

// Reload-on-chunk-error: when a deploy ships and a returning user still has
// the old SPA shell cached, dynamic imports for new routes resolve to
// content-hashed chunk filenames that no longer exist on the server (we
// saw ~41 distinct dead chunks 404 over a 4-day window in prod log review).
// The user gets a blank page mid-navigation. Detect the typical Vite +
// modern-browser error shapes and force-reload once per session; the
// sessionStorage guard prevents an infinite loop if the build is genuinely
// broken (we surface that crash to the error boundary instead).
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];
const CHUNK_RELOAD_FLAG = '__chunk-reload-attempted';
const handleChunkError = (msg: string | undefined) => {
  if (!msg) return false;
  if (!CHUNK_ERROR_PATTERNS.some((re) => re.test(msg))) return false;
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return false;
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
  window.location.reload();
  return true;
};
window.addEventListener('error', (event) => {
  handleChunkError(event.message || (event.error as Error | undefined)?.message);
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { message?: string } | string | undefined;
  const msg = typeof reason === 'string' ? reason : reason?.message;
  handleChunkError(msg);
});
// Clear the flag a few seconds after successful navigation so a *future*
// deploy doesn't get suppressed by the previous run's flag. Guard against
// the case where `load` already fired before this script ran (defer +
// slow parse) — without this check the flag would stick for the whole
// session and the next chunk failure would be silently swallowed.
const scheduleFlagClear = () => {
  setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_FLAG), 5000);
};
if (document.readyState === 'complete') {
  scheduleFlagClear();
} else {
  window.addEventListener('load', scheduleFlagClear, { once: true });
}

const tree = (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </BrowserRouter>
  </QueryClientProvider>
)

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {instantTasksEnabled && instantTasksOptions ? (
      <InstantTasksProvider options={instantTasksOptions as any}>{tree}</InstantTasksProvider>
    ) : (
      tree
    )}
  </React.StrictMode>,
)
